import { InfoCircleOutlined } from "@ant-design/icons"
import { Button, Drawer, Flex, Spin, Tag, Tooltip } from "antd"
import React, { useState } from "react"

import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import type { DocumentTemplateMutateData } from "../../types/types.ts"

interface DocumentTemplateFormPreviewProps {
  loading?: boolean
  submit: () => void
  onPreview: (payload: DocumentTemplateMutateData) => Promise<string> // html
  onValidate: () => Promise<DocumentTemplateMutateData | null>
}

export function DocumentTemplateFormPreview({
  loading,
  submit,
  onPreview,
  onValidate
}: DocumentTemplateFormPreviewProps) {
  const { normalizeDate } = useDateFormat()
  const [open, setOpen] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [html, setHtml] = useState<string | null>(null)

  const onPreviewClick = async () => {
    const values = await onValidate()
    if (!values) return

    try {
      setFetching(true)
      const result = await onPreview({
        ...values,
        start_date: normalizeDate(values?.start_date),
        completion_date: normalizeDate(values?.completion_date),
        _meta: { doc_type: values?.doc_type }
      })
      setHtml(result)
      setOpen(true)
    } catch (e) {
      console.error(e)
    } finally {
      setFetching(false)
    }
  }

  const onClose = () => {
    setOpen(false)
    setHtml(null)
  }

  return (
    <>
      <Button loading={loading || fetching} onClick={onPreviewClick}>
        Preview
      </Button>

      <Drawer
        classNames={{ body: "p-1" }}
        open={open}
        width="100vw"
        onClose={onClose}
        title={
          <Flex align="center" gap="small">
            <span>Template Preview</span>
            <Tooltip title="Some fields are filled with your data, others may use placeholder values. This is not the final document.">
              <Tag color="warning" icon={<InfoCircleOutlined />}>
                May contain sample data
              </Tag>
            </Tooltip>
          </Flex>
        }
      >
        {fetching && <Spin fullscreen />}
        <Flex vertical gap={"small"} className={"size-full"}>
          <iframe
            srcDoc={html ?? ""}
            className="flex-1 border-0"
            sandbox="allow-same-origin"
            title="Preview"
          />
          <Flex justify={"end"} className={"p-2"}>
            <Button
              loading={loading || fetching}
              type="primary"
              htmlType={"submit"}
              onClick={submit}
            >
              Save Template
            </Button>
          </Flex>
        </Flex>
      </Drawer>
    </>
  )
}
